'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Close } from '@shared/icons';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../lib/utils';

type DialogVariant = 'modal' | 'dialog';

const DialogContext = React.createContext<{ variant?: DialogVariant; showCloseButton?: boolean }>({});

function Dialog({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  const CHROMA_CLASSES = 'bg-overlay-background/80 backdrop-blur-[8px]';

  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50',
        CHROMA_CLASSES,
        className
      )}
      {...props}
    />
  );
}

const dialogContentVariants = cva(
  'bg-background data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 fixed z-50 w-full gap-4 p-6 shadow-lg duration-200',
  {
    variants: {
      variant: {
        modal:
          'bg-background inset-0 h-full flex flex-col md:inset-auto md:top-1/2 md:left-1/2 md:h-fit md:max-w-lg md:max-h-[min(800px,calc(100vh-2rem))] md:-translate-x-1/2 md:-translate-y-1/2 rounded-none md:rounded-lg',
        dialog:
          'bg-container-base flex flex-col top-1/2 left-1/2 max-w-[calc(100%-2rem)] max-h-[calc(100vh-2rem)] sm:max-w-[425px] sm:max-h-[min(800px,calc(100vh-2rem))] -translate-x-1/2 -translate-y-1/2 rounded-lg border border-solid border-border',
      },
    },
    defaultVariants: {
      variant: 'modal',
    },
  }
);

function DialogContent({
  className,
  customOverlayClassName,
  children,
  showCloseButton = true,
  variant,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & { customOverlayClassName?: string; showCloseButton?: boolean } & VariantProps<typeof dialogContentVariants>) {
  const CHROMA_CLASSES = 'focus:outline-none focus-visible:outline-none sm:border sm:border-solid sm:border-border';
  const contextValue = React.useMemo(() => ({ variant: variant ?? undefined, showCloseButton }), [variant, showCloseButton]);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className={customOverlayClassName} />
      <DialogContext.Provider value={contextValue}>
        <DialogPrimitive.Content data-slot="dialog-content" className={cn(dialogContentVariants({ variant }), CHROMA_CLASSES, className)} {...props}>
          {children}
          {showCloseButton && (
            <DialogPrimitive.Close
              data-slot="dialog-close"
              className="ring-offset-background focus:ring-ring data-[state=open]:bg-accent data-[state=open]:text-muted-foreground absolute top-4 right-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4"
              data-track-name="Close"
              data-testid="dialog-close-button"
            >
              <Close size="small" />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          )}
        </DialogPrimitive.Content>
      </DialogContext.Provider>
    </DialogPortal>
  );
}

/**
 * DialogHeader variant configuration using class-variance-authority
 */
const dialogHeaderVariants = cva('', {
  variants: {
    variant: {
      modal: 'flex flex-col gap-1 -mt-2 -mx-6 px-6 border-b border-border pb-4 flex-shrink-0', // Modal variant: border and title aligned with close button
      dialog: 'block flex-shrink-0', // Dialog variant: no border, title flows normally
    },
  },
  defaultVariants: {
    variant: 'modal',
  },
});

function DialogHeader({ className, variant, ...props }: React.ComponentProps<'div'> & VariantProps<typeof dialogHeaderVariants>) {
  const context = React.useContext(DialogContext);
  const resolvedVariant = variant ?? context.variant;
  const showCloseButton = context.showCloseButton ?? true;
  return (
    <div
      data-slot="dialog-header"
      className={cn(dialogHeaderVariants({ variant: resolvedVariant }), !showCloseButton && resolvedVariant === 'modal' && 'items-center', className)}
      {...props}
    />
  );
}

/**
 * DialogFooter variant configuration using class-variance-authority
 */
const dialogFooterVariants = cva('flex gap-4', {
  variants: {
    variant: {
      modal: 'pt-6 flex-col-reverse md:flex-row md:justify-end flex-shrink-0', // Mobile: stacked vertically, Desktop: side-by-side right-aligned
      dialog: 'pt-6 flex-row justify-end flex-shrink-0', // Always side-by-side, right-aligned across all breakpoints
    },
  },
  defaultVariants: {
    variant: 'modal',
  },
});

function DialogFooter({ className, variant, ...props }: React.ComponentProps<'div'> & VariantProps<typeof dialogFooterVariants>) {
  const context = React.useContext(DialogContext);
  const resolvedVariant = variant ?? context.variant;
  return <div data-slot="dialog-footer" className={cn(dialogFooterVariants({ variant: resolvedVariant }), className)} {...props} />;
}

/**
 * DialogTitle variant configuration using class-variance-authority
 */
const dialogTitleVariants = cva('', {
  variants: {
    variant: {
      modal: 'paragraph-explanatory-strong',
      dialog: 'heading-5',
    },
  },
  defaultVariants: {
    variant: 'modal',
  },
});

function DialogTitle({ className, variant, children, ...props }: React.ComponentProps<typeof DialogPrimitive.Title> & VariantProps<typeof dialogTitleVariants>) {
  const context = React.useContext(DialogContext);
  const resolvedVariant = variant ?? context.variant;
  const showCloseButton = context.showCloseButton ?? true;
  return (
    <DialogPrimitive.Title asChild>
      <h5 data-slot="dialog-title" className={cn(dialogTitleVariants({ variant: resolvedVariant }), !showCloseButton && 'w-full text-center', className)} {...props}>
        {children}
      </h5>
    </DialogPrimitive.Title>
  );
}

/**
 * DialogDescription variant configuration using class-variance-authority
 * Both variants: flex-1 min-h-0 overflow-y-auto so the description takes only the remaining space between
 * header and footer, scrolls when content overflows, and keeps footer and DialogContent p-6 visible.
 */
const dialogDescriptionVariants = cva('text-foreground-subtle min-h-0 flex-1 overflow-y-auto', {
  variants: {
    variant: {
      modal: '',
      dialog: 'paragraph-explanatory',
    },
  },
  defaultVariants: {
    variant: 'modal',
  },
});

function DialogDescription({ className, variant, ...props }: React.ComponentProps<typeof DialogPrimitive.Description> & VariantProps<typeof dialogDescriptionVariants>) {
  const context = React.useContext(DialogContext);
  const resolvedVariant = variant ?? context.variant;
  return <DialogPrimitive.Description data-slot="dialog-description" className={cn(dialogDescriptionVariants({ variant: resolvedVariant }), className)} {...props} />;
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  dialogContentVariants,
  DialogDescription,
  dialogDescriptionVariants,
  DialogFooter,
  dialogFooterVariants,
  DialogHeader,
  dialogHeaderVariants,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  dialogTitleVariants,
  DialogTrigger,
};
